// The gear that turns the viewer's two shader effects on and off
// (owner, 2026-09-19: "so we can turn these effects on and off"), on
// every page that draws a model.
//
// The choice follows the PERSON, not the browser: it is kept with the
// rest of their remembered state (helpers/userPrefs), so it holds across
// pages, sessions and machines. It is deliberately not per project - it
// is a way of looking at a model, like a display setting.
//
// The pages differ in how they hold their viewer (a ref, a piece of
// state, a getter), so `viewer` takes any of the three; and they differ
// in what their toolbar looks like, so `variant` picks the markup:
//   "mdb"      - the white MDBBtn row on the model, timeline, socket and
//                asset viewers
//   "dark"     - the small dark square buttons on the issue canvas
//   "floating" - its own round button, for a page with no toolbar at all
//                (Create issue, Align)
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Popover, Radio, Slider, Switch, Tooltip } from "antd";
import { SettingOutlined } from "@ant-design/icons";
import { MDBIcon } from "mdbreact";
import { usePreference } from "helpers/userPrefs";
import { shouldIgnoreKey } from "webgl/js/viewer/keyGuard";

export const EDGE_PREF = "viewer.edgeLines";
export const CONTACT_PREF = "viewer.contactShade";
// Depth-aware drawing: a second pass that knows what is BEHIND what, so
// an outline appears where one thing crosses another and the thickness
// is a real choice. The cheap version can only ever draw one pixel.
export const DEPTH_PREF = "viewer.depthAware";
// Clearance zones: the empty volume a piece of equipment needs around it,
// exported as translucent shells. OFF by default - a model full of red and
// yellow boxes is not what anybody opens a model to see - and remembered per
// person like the rest, because it is a way of looking at a model rather
// than a property of one.
export const CLEARANCE_PREF = "viewer.clearanceZones";
export const THICKNESS_PREF = "viewer.edgeThickness";
export const SHADE_PREF = "viewer.shadeStrength";

// The viewer this button drives, however the page happens to hold it.
function resolveViewer(viewer) {
  try {
    const held = typeof viewer === "function" ? viewer() : viewer;
    if (!held) return null;
    if (typeof held.setShading === "function") return held;
    // a React ref, or a WebGlApp that keeps the viewer on `.viewer`
    const inner = held.current || held.viewer;
    if (inner && typeof inner.setShading === "function") return inner;
    if (inner && inner.viewer && typeof inner.viewer.setShading === "function") return inner.viewer;
  } catch (e) { /* a page tearing down */ }
  return null;
}

// The clearance switch talks to the STREAMING LOADER rather than to the
// viewer: which rows are drawn is a loading decision, not a shading one. The
// loader hangs off its own viewer so a page with two of them drives the right
// one; window.__sl is the fallback for a viewer built before that landed.
// SAY WHAT IS COVERED, DO NOT IMPLY ALL OF IT. Clearance is flagged when a
// file is gathered, so a model whose links were banked before that existed
// has none in them - and hiding "clearance zones" would then hide a fraction
// of them while reading as complete. The numbers come from the loaded rows,
// so this widens by itself when those files are re-gathered.
export function clearanceHint(coverage) {
  const base = "Shows the space equipment needs around it, as translucent shells. Off by default - they are an overlay, not part of the model.";
  if (!coverage) return base;
  const { filesWithClearance, filesSeen, rows } = coverage;
  if (!rows) {
    return base + " Nothing loaded so far carries clearance data"
      + (filesSeen ? " (" + filesSeen + " model file" + (filesSeen === 1 ? "" : "s") + " seen)" : "")
      + ", so this switch has nothing to show yet.";
  }
  if (filesSeen && filesWithClearance < filesSeen) {
    return base + " Found in " + filesWithClearance + " of " + filesSeen
      + " model files loaded - the others were exported before clearance was recorded, so their zones are not shown.";
  }
  return base + " Found in every model file loaded so far.";
}

function resolveLoader(viewer) {
  try {
    const v = resolveViewer(viewer);
    if (v && v.streamingLoader && typeof v.streamingLoader.setClearanceVisible === "function") {
      return v.streamingLoader;
    }
    const g = typeof window !== "undefined" ? window.__sl : null;
    if (g && typeof g.setClearanceVisible === "function") return g;
  } catch (e) { /* a page tearing down */ }
  return null;
}

export default function ViewerEffectsButton({ viewer, variant = "mdb", style = null, placement = "bottomRight" }) {
  const [clearance, setClearance] = usePreference(CLEARANCE_PREF, false);
  // What the toggle can actually see. Shown beside the switch rather than
  // assumed, because clearance is flagged per gather: on SpaceX today the
  // host section carries it and all sixteen links carry none, so a toggle
  // that promised "clearance zones" would be blind on most of the model and
  // look like it was working.
  const [coverage, setCoverage] = useState(null);
  const [edges, setEdges] = usePreference(EDGE_PREF, false);
  const [contact, setContact] = usePreference(CONTACT_PREF, false);
  const [depth, setDepth] = usePreference(DEPTH_PREF, false);
  const [thickness, setThickness] = usePreference(THICKNESS_PREF, 1);
  const [shade, setShade] = usePreference(SHADE_PREF, 45);
  const [open, setOpen] = useState(false);

  // Coverage is re-read while the popover is open: tiles stream in, so a
  // number taken once at mount would be wrong by the time anybody read it.
  useEffect(() => {
    if (!open) return undefined;
    const read = () => {
      const l = resolveLoader(viewer);
      setCoverage(l && typeof l.clearanceCoverage === "function" ? l.clearanceCoverage() : null);
    };
    read();
    const t = setInterval(read, 1500);
    return () => clearInterval(t);
  }, [open, viewer]);

  // Clearance is pushed on its own, because it is a different destination
  // and a different cost: showing or hiding it re-materialises the tiles that
  // carry it, so this must not fire on every render - only when the
  // preference actually differs from what the loader is doing.
  useEffect(() => {
    let cancelled = false;
    const apply = () => {
      if (cancelled) return true;
      const l = resolveLoader(viewer);
      if (!l) return false;
      if (l.clearanceVisible() === !!clearance) return true;   // already right
      l.setClearanceVisible(!!clearance);
      return true;
    };
    if (apply()) return undefined;
    // the model may still be loading; try again as it settles
    const t = setInterval(() => { if (apply()) clearInterval(t); }, 600);
    return () => { cancelled = true; clearInterval(t); };
  }, [clearance, viewer]);

  // Carried to the viewer whenever either side changes. A viewer that is
  // still being built is picked up by the retry below, so a preference
  // set on a previous visit is applied as soon as the model is there.
  const push = useCallback((next) => {
    const v = resolveViewer(viewer);
    if (!v) return false;
    v.setShading(next);
    return true;
  }, [viewer]);

  useEffect(() => {
    const strength = Math.max(0, Math.min(1, (Number(shade) || 0) / 100));
    const next = {
      edges: !!edges, contact: !!contact, contactStrength: strength,
      // The depth pass draws its own edges and shade, so the cheap ones
      // stand down while it is on rather than both darkening the same
      // pixel twice.
      composite: !!depth && (!!edges || !!contact),
      edgePx: Math.max(1, Math.min(3, Number(thickness) || 1)),
      edge: depth && edges ? 0.85 : 0,
      ao: depth && contact ? strength : 0,
    };
    if (push(next)) return undefined;
    // A big model can take MINUTES to put a viewer on the page, and the
    // first version of this gave up after 30 seconds - so a preference
    // set on a previous visit silently never arrived, and the person's
    // own toggle was the only thing that ever applied it. It now keeps
    // trying until the component goes away, backing off so a page
    // without a viewer costs nothing.
    let wait = 400;
    let timer = null;
    const tick = () => {
      if (push(next)) return;
      wait = Math.min(5000, Math.round(wait * 1.4));
      timer = setTimeout(tick, wait);
    };
    timer = setTimeout(tick, wait);
    return () => { if (timer) clearTimeout(timer); };
  }, [edges, contact, depth, thickness, shade, push]);

  // [D] like the viewer's other tools ([M] measure, [C] section): the
  // owner could not find the button among labelled ones, and a label
  // with no shortcut behind it would be a lie.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "d" && e.key !== "D") return;
      // The same rule the viewer's own shortcuts use, so there is one
      // answer to "should this letter drive the model?".
      if (shouldIgnoreKey(e)) return;
      setOpen((v) => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const on = !!edges || !!contact;

  const body = useMemo(() => (
    <div style={{ width: 286 }}>
      <Row
        label="Edge lines"
        hint="Darkens the corners and outlines of everything drawn, so shapes read apart without a wireframe."
        checked={!!edges}
        onChange={setEdges}
      />
      <Row
        label="Clearance zones"
        hint={clearanceHint(coverage)}
        checked={!!clearance}
        onChange={setClearance}
      />
      <Row
        label="Contact shade"
        hint="Darkens the inside of corners and creases - where a wall meets a floor, or a duct folds."
        checked={!!contact}
        onChange={setContact}
      />
      {contact && (
        <div style={{ padding: "2px 0 8px 34px" }}>
          <div style={{ fontSize: 11.5, color: "#8c8c8c", marginBottom: 2 }}>Shade strength</div>
          <Slider min={0} max={100} value={Number(shade) || 0} onChange={setShade}
            tooltip={{ formatter: (v) => `${v}%` }} />
        </div>
      )}
      <Row
        label="Depth-aware"
        hint="Reads how far away things are, so an outline also appears where one thing crosses another. Costs a second pass - turn it off if the model feels heavy."
        checked={!!depth}
        onChange={setDepth}
      />
      {depth && (edges || contact) && (
        <div style={{ padding: "2px 0 4px 34px" }}>
          <div style={{ fontSize: 11.5, color: "#8c8c8c", marginBottom: 4 }}>Line thickness</div>
          <Radio.Group size="small" value={Number(thickness) || 1} onChange={(e) => setThickness(e.target.value)}>
            <Radio.Button value={1}>1 px</Radio.Button>
            <Radio.Button value={2}>2 px</Radio.Button>
            <Radio.Button value={3}>3 px</Radio.Button>
          </Radio.Group>
          <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 4, lineHeight: 1.35 }}>
            2 and 3 px start to fill in thin members - good for a printed
            view, heavy on a busy model.
          </div>
        </div>
      )}
      <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 8, lineHeight: 1.4 }}>
        Drawn by the model's own shader: nothing is downloaded, and the
        choice is remembered for you on every viewer.
      </div>
    </div>
  // clearance and coverage BELONG HERE. Without them the memo builds the
  // popover once and never again: the switch still toggled - its handler is
  // stable - but `checked` stayed false and the hint kept saying the generic
  // description while the loader was reporting real coverage. A memo that is
  // missing a dependency does not look broken, it looks like the data never
  // changed, which is why a unit test on the hint passed and only rendering
  // it showed the fault.
  ), [edges, contact, depth, thickness, shade, clearance, coverage,
      setEdges, setContact, setDepth, setThickness, setShade, setClearance]);

  const trigger = (
    <Popover
      content={body}
      title="Display"
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement={placement}
    >
      {renderButton(variant, on, style)}
    </Popover>
  );
  return trigger;
}

function Row({ label, hint, checked, onChange }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "6px 0" }}>
      <Switch size="small" checked={checked} onChange={onChange} style={{ marginTop: 2 }} />
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#262626" }}>{label}</div>
        <div style={{ fontSize: 11.5, color: "#8c8c8c", lineHeight: 1.35 }}>{hint}</div>
      </div>
    </div>
  );
}

function renderButton(variant, on, style) {
  if (variant === "dark") {
    return (
      <button
        type="button"
        id="viewerEffectsBtn"
        title="Display - edge lines and contact shade"
        style={{
          width: 28, height: 28, borderRadius: 4, border: "none",
          background: on ? "rgba(254,105,25,.9)" : "rgba(255,255,255,.08)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", cursor: "pointer", ...(style || {}),
        }}
      >
        <SettingOutlined />
      </button>
    );
  }
  if (variant === "floating") {
    return (
      <Tooltip title="Display - edge lines and contact shade">
        <button
          type="button"
          id="viewerEffectsBtn"
          style={{
            width: 32, height: 32, borderRadius: 16, cursor: "pointer",
            border: "1px solid rgba(0,0,0,.12)", background: on ? "#fe6919" : "#fff",
            color: on ? "#fff" : "#262626", boxShadow: "0 2px 6px rgba(0,0,0,.18)",
            display: "flex", alignItems: "center", justifyContent: "center",
            ...(style || {}),
          }}
        >
          <SettingOutlined />
        </button>
      </Tooltip>
    );
  }
  // The white toolbar the four big viewers share. Same markup as its
  // neighbours - same classes, the same FontAwesome icon family, and a
  // bracket letter - because a button that looks unlike every button
  // beside it does not read as a button at all (owner, 2026-09-19:
  // "the viewer doesn't have the gear icon", when it was there).
  return (
    <button
      type="button"
      id="viewerEffectsBtn"
      title="Display: edge lines and contact shade [D]"
      className="btn btn-sm bg-white modal3d-tool-btn"
      style={{ color: on ? "#fe6919" : undefined, ...(style || {}) }}
    >
      <MDBIcon icon="sliders-h" className="font-size-1" />
      &nbsp; <b>[D] </b>
    </button>
  );
}
